/**
 * The display-name rule, mirrored from the server's
 * `flowstack.auth.user_profile.normalize_display_name` (flowstack#2002) so the
 * Settings form can refuse a bad name before sending it. The server stays the
 * authority: its refusal is shown verbatim when the two disagree.
 */

export const DISPLAY_NAME_MAX_LENGTH = 80;

/** C0, DEL, C1, the line/paragraph separators, lone surrogates, the bidi
 *  embedding / override / isolate controls and marks, and the zero-width
 *  space, word joiner and byte-order mark. The joiners (U+200C, U+200D) stay
 *  allowed. */
const CONTROL =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001f\u007f-\u009f\u2028\u2029\p{Cs}\u202a-\u202e\u2066-\u2069\u200e\u200f\u061c\u200b\u2060\ufeff]/u;

/** One character that draws something: neither a format character nor a space. */
const VISIBLE = /[^\p{Cf}\p{Zs}]/u;

/** Why `raw` would be refused, or `null` when it is acceptable. An empty or
 *  blank value is acceptable: it clears the override. */
export function displayNameProblem(raw: string): string | null {
  const name = raw.trim();
  if (name === "") return null;
  // Characters, not UTF-16 units — the server counts code points.
  if ([...name].length > DISPLAY_NAME_MAX_LENGTH) {
    return `A display name is at most ${DISPLAY_NAME_MAX_LENGTH} characters.`;
  }
  if (CONTROL.test(name)) return "A display name cannot contain control characters.";
  if (!VISIBLE.test(name)) return "A display name needs at least one visible character.";
  return null;
}
